import apiClient from '@/lib/apiClient';

const BASE = '/v1/admin/i18n';

export interface TranslationEntry {
  key: string;
  namespace: string;
  translations: Record<string, string>;
  updatedBy?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface TranslationListResponse {
  content: TranslationEntry[];
  totalItems: number;
  page: number;
  size: number;
  namespaces: string[];
  /** locale codes supported by the backend (from the V1012 supported_locales table). */
  locales: string[];
}

export interface TranslationListParams {
  page: number;
  size: number;
  namespace?: string;
  search?: string;
}

export interface TranslationCreateInput {
  key: string;
  namespace: string;
  translations: Record<string, string>;
}

export interface TranslationUpdateInput {
  namespace?: string;
  translations?: Record<string, string>;
}

export interface SupportedLocale {
  code: string;
  name: string;
  nativeName?: string;
  rtl: boolean;
  fallback?: string | null;
}

export interface CoverageNamespaceRow {
  namespace: string;
  totalKeys: number;
  perLocale: Record<string, { translated: number; missing: number; coveragePct: number }>;
}

export interface CoverageResponse {
  totalKeys: number;
  locales: string[];
  overall: { locale: string; translated: number; missing: number; coveragePct: number }[];
  namespaces: CoverageNamespaceRow[];
}

export interface MissingResponse {
  locale: string;
  namespace?: string;
  limit: number;
  missing: { key: string; namespace: string }[];
}

export interface ImportEntry {
  namespace: string;
  key: string;
  translations: Record<string, string>;
}

export interface ImportRequest {
  mode: 'SKIP_EXISTING' | 'OVERWRITE';
  entries: ImportEntry[];
}

export interface ImportResult {
  mode: string;
  entries: number;
  keysCreated: number;
  keysExisted: number;
  valuesInserted: number;
  valuesUpdated: number;
  valuesSkipped: number;
  badEntries: number;
  errors: { key: string; reason: string }[];
  errorsTruncated: boolean;
}

export const translationsApi = {
  async list(params: TranslationListParams): Promise<TranslationListResponse> {
    const res = await apiClient.get<TranslationListResponse>(BASE, {
      params: {
        page: params.page,
        size: params.size,
        ...(params.namespace ? { namespace: params.namespace } : {}),
        ...(params.search ? { search: params.search } : {}),
      },
    });
    return res.data;
  },

  async create(input: TranslationCreateInput): Promise<TranslationEntry> {
    const res = await apiClient.post<TranslationEntry>(BASE, input);
    return res.data;
  },

  async update(key: string, input: TranslationUpdateInput): Promise<TranslationEntry> {
    const res = await apiClient.put<TranslationEntry>(`${BASE}/${encodeURIComponent(key)}`, input);
    return res.data;
  },

  async remove(key: string): Promise<void> {
    await apiClient.delete(`${BASE}/${encodeURIComponent(key)}`);
  },

  async locales(): Promise<SupportedLocale[]> {
    const res = await apiClient.get<SupportedLocale[]>(`${BASE}/locales`);
    return res.data;
  },

  async coverage(): Promise<CoverageResponse> {
    const res = await apiClient.get<CoverageResponse>(`${BASE}/coverage`);
    return res.data;
  },

  async missing(locale: string, namespace?: string, limit = 100): Promise<MissingResponse> {
    const res = await apiClient.get<MissingResponse>(`${BASE}/missing`, {
      params: { locale, ...(namespace ? { namespace } : {}), limit },
    });
    return res.data;
  },

  async export(namespace?: string): Promise<ImportEntry[]> {
    const res = await apiClient.get<ImportEntry[]>(`${BASE}/export`, {
      params: namespace ? { namespace } : undefined,
    });
    return res.data;
  },

  async import(request: ImportRequest): Promise<ImportResult> {
    const res = await apiClient.post<ImportResult>(`${BASE}/import`, request);
    return res.data;
  },
};
